/**
 * V147 (IMPRESION) — Personalización del ticket que ve el cliente (venta/cancelación).
 * Logo (se sube, se convierte a MONOCROMO server-side; el preview muestra la versión
 * CONVERTIDA), encabezado/pie con contador de caracteres y líneas, QR de facturación
 * (toggle) + QR configurable (URL + leyenda). Preview completo a 58 y 80mm + prueba.
 * La comanda de cocina NO se personaliza.
 */
import { useEffect, useRef, useState } from 'react';
import { Image as ImageIcon, Trash2, RefreshCw, Play, AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import QRCode from 'qrcode';
import { salesTicket } from '../lib/escpos';
import { salesTicketHtml, SalesTicketData, PaperWidth } from './ticket';
import {
  TicketSettings, fetchTicketSettings, saveTicketSettings, uploadTicketLogo, deleteTicketLogo, resolveBranding,
} from './config';
import { printTicket } from '../gastrogest/printing';
import type { PrintOutcome } from './router';

const TEXT_MAX = 240;
const btnPrimary = 'inline-flex items-center gap-1.5 rounded-xl bg-[var(--brand-primary)] px-3 py-2 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[var(--brand-primary)]';
const btnGhost = 'inline-flex items-center gap-1.5 rounded-xl bg-white px-3 py-2 text-xs font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white';

const emptySettings = (branchId: string | null): TicketSettings => ({
  branchId, headerText: '', footerText: '', showInvoiceQr: true, showIssuerLine: true, printComanda: true, showCustomQr: false,
  customQrUrl: '', customQrLabel: '', qrSizeMm: 30, logoHeightMm: 18,
  logoMono58: null, logoEscpos58: null, logoMono80: null, logoEscpos80: null,
});

/** Estima cuántas líneas ocupa un texto a `cols` de ancho (respeta saltos de línea). */
function estLines(text: string, cols: number): number {
  return (text || '').split('\n').reduce((a, l) => a + Math.max(1, Math.ceil(l.length / cols)), 0);
}

/** Campo de texto a NIVEL DE MÓDULO (si se define dentro del componente, React lo
 *  remonta en cada tecla y el textarea pierde el foco → "brinca"). */
function TextField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="block text-sm">
      <span className="text-xs font-medium text-slate-600">{label}</span>
      <textarea className="input mt-1" rows={2} maxLength={TEXT_MAX} value={value} onChange={(e) => onChange(e.target.value)} />
      <span className="mt-0.5 block text-[11px] text-slate-400">
        {value.length}/{TEXT_MAX} · ~{estLines(value, 32)} líneas en 58mm · ~{estLines(value, 48)} en 80mm
      </span>
    </label>
  );
}

const SAMPLE_ITEMS = [
  { qty: 2, description: 'Taco al pastor', lineTotal: 70 },
  { qty: 1, description: 'Agua de horchata', lineTotal: 25 },
];

/** Factura de MUESTRA (para preview y prueba) — QR + código + emisor + plazo, en ESC/POS y HTML.
 *  `showIssuer` refleja el toggle del tenant: con false, el emisor no va (pero el plazo sí). */
async function buildSampleInvoice(brandName: string, showIssuer = true) {
  const url = 'https://ejemplo.mx/factura/DEMO1234';
  const qrDataUrl = await QRCode.toDataURL(url, { margin: 1, width: 240 });
  const common = {
    code: 'DEMO-1234', urlLabel: 'ejemplo.mx/factura/DEMO1234',
    issuerRazon: showIssuer ? brandName : null,
    issuerRfc: showIssuer ? 'XAXX010101000' : null,
    windowLabel: 'Puedes facturar esta compra hasta fin de mes',
  };
  return { esc: { ...common, qrUrl: url }, html: { ...common, qrDataUrl } };
}

export function TicketCustomization({ branchId = null, brandName = 'Mi negocio', canManage = true }:
  { branchId?: string | null; brandName?: string; canManage?: boolean }) {
  const [s, setS] = useState<TicketSettings>(emptySettings(branchId));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [testResult, setTestResult] = useState<PrintOutcome | null>(null);
  const [preview, setPreview] = useState<{ w58: string; w80: string }>({ w58: '', w80: '' });
  const fileRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    setLoading(true); setErr(null);
    try { setS({ ...emptySettings(branchId), ...(await fetchTicketSettings(branchId, false)) }); }
    catch (e: any) { setErr(e?.body?.message || e?.message || 'No se pudo cargar la personalización'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [branchId]);

  // Recalcula el preview (async por el QR). Debounce para no recalcular en cada tecla.
  useEffect(() => {
    let alive = true;
    const t = window.setTimeout(async () => {
      const branding = await resolveBranding(s);
      const invoice = s.showInvoiceQr ? (await buildSampleInvoice(brandName, s.showIssuerLine)).html : null;
      const data = (w: PaperWidth): SalesTicketData => ({
        brandName,
        delivery: { code: 'A7', ref: 'Ana', statusLabel: 'Listo para recoger' },
        items: SAMPLE_ITEMS,
        subtotal: 81.9, taxTotal: 13.1, total: 95, tip: 10, grandTotal: 105,
        methodLabel: 'Efectivo', cash: { received: 120, change: 15 },
        invoice, branding: branding.htmlFor(w), qrSizeMm: branding.qrSizeMm,
      });
      if (!alive) return;
      setPreview({ w58: salesTicketHtml(data(58), 58, false), w80: salesTicketHtml(data(80), 80, false) });
    }, 250);
    return () => { alive = false; window.clearTimeout(t); };
  }, [s, brandName]);

  const set = (patch: Partial<TicketSettings>) => setS((p) => ({ ...p, ...patch }));

  const save = async () => {
    setSaving(true); setErr(null); setMsg(null);
    try {
      const updated = await saveTicketSettings({
        branchId, headerText: s.headerText, footerText: s.footerText,
        showInvoiceQr: s.showInvoiceQr, showIssuerLine: s.showIssuerLine, printComanda: s.printComanda, showCustomQr: s.showCustomQr,
        customQrUrl: s.customQrUrl || undefined, customQrLabel: s.customQrLabel,
        qrSizeMm: s.qrSizeMm, logoHeightMm: s.logoHeightMm,
      });
      setS((p) => ({ ...p, ...updated }));   // refleja el logo re-convertido si cambió el alto
      setMsg('Guardado.');
    } catch (e: any) { setErr(e?.body?.message || e?.message || 'No se pudo guardar'); }
    finally { setSaving(false); }
  };

  const onLogo = async (file: File) => {
    setLogoBusy(true); setErr(null); setMsg(null);
    try {
      const updated = await uploadTicketLogo(branchId, file);
      setS((p) => ({ ...p, ...updated }));
      setMsg('Logo convertido a monocromo y guardado.');
    } catch (e: any) { setErr(e?.body?.message || e?.message || 'No se pudo subir el logo'); }
    finally { setLogoBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  };
  const removeLogo = async () => {
    setLogoBusy(true); setErr(null);
    try { const u = await deleteTicketLogo(branchId); setS((p) => ({ ...p, ...u })); }
    catch (e: any) { setErr(e?.body?.message || e?.message); }
    finally { setLogoBusy(false); }
  };

  const test = async () => {
    setTestResult(null);
    const branding = await resolveBranding(s);
    const inv = s.showInvoiceQr ? await buildSampleInvoice(brandName, s.showIssuerLine) : null;
    const base = {
      brandName, delivery: null, items: SAMPLE_ITEMS,
      subtotal: 81.9, taxTotal: 13.1, total: 95, tip: 10, grandTotal: 105,
      methodLabel: 'Efectivo', cash: { received: 120, change: 15 },
    };
    const r = await printTicket({
      branchId, kind: 'ticket',
      escpos: (w) => salesTicket({ ...base, invoice: inv?.esc ?? null, branding: branding.escFor(w), qrModule: branding.qrModule, cols: w === 58 ? 32 : 48 }),
      html: (w) => salesTicketHtml({ ...base, invoice: inv?.html ?? null, branding: branding.htmlFor(w), qrSizeMm: branding.qrSizeMm }, w),
    });
    setTestResult(r);
  };

  if (loading) return <div className="mx-auto max-w-4xl"><div className="h-40 animate-pulse rounded-xl bg-slate-100" /></div>;

  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <header>
        <h2 className="flex items-center gap-2 font-serif text-2xl tracking-tight text-slate-900">
          <ImageIcon className="h-6 w-6 text-slate-400" /> Personalización del ticket
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          Aplica al ticket de venta y a la cancelación (lo que ve el cliente). La comanda de cocina no se personaliza.
        </p>
      </header>

      {err && <div className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700 ring-1 ring-rose-200">{err}</div>}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr,320px]">
        {/* Formulario */}
        <div className="space-y-5">
          {/* Logo */}
          <section className="rounded-xl border border-slate-200 bg-white p-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Logo</p>
            <p className="mt-1 flex items-start gap-1.5 text-xs text-slate-500">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Las térmicas son monocromo. Convertimos tu logo a blanco y negro (con dithering) — abajo ves cómo saldrá en papel, no el original.
            </p>
            <div className="mt-3 flex items-center gap-3">
              {s.logoMono80 ? (
                <img src={s.logoMono80} alt="logo mono" className="max-h-16 rounded border border-slate-200 bg-white p-1" style={{ imageRendering: 'pixelated' }} />
              ) : (
                <div className="grid h-16 w-24 place-items-center rounded border border-dashed border-slate-300 text-[11px] text-slate-400">Sin logo</div>
              )}
              {canManage && (
                <div className="flex flex-col gap-2">
                  <input ref={fileRef} type="file" accept="image/png,image/jpeg" className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) onLogo(f); }} />
                  <button className={btnGhost} onClick={() => fileRef.current?.click()} disabled={logoBusy}>
                    {logoBusy ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />} Subir logo (PNG/JPEG)
                  </button>
                  {s.logoMono80 && <button className={btnGhost} onClick={removeLogo} disabled={logoBusy}><Trash2 className="h-3.5 w-3.5 text-rose-600" /> Quitar</button>}
                </div>
              )}
            </div>
            {canManage && (
              <label className="mt-3 flex items-center gap-2 text-sm">
                <span className="text-xs font-medium text-slate-600">Alto del logo</span>
                <select className="input w-auto" value={s.logoHeightMm} onChange={(e) => set({ logoHeightMm: Number(e.target.value) })}>
                  <option value={12}>Pequeño (~12 mm)</option>
                  <option value={18}>Mediano (~18 mm)</option>
                  <option value={25}>Grande (~25 mm)</option>
                  <option value={30}>Extra (~30 mm)</option>
                </select>
                <span className="text-[11px] text-slate-400">Guarda para aplicar el nuevo alto</span>
              </label>
            )}
          </section>

          {/* Textos */}
          <section className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Textos</p>
            <TextField label="Encabezado (bajo el logo)" value={s.headerText ?? ''} onChange={(v) => set({ headerText: v })} />
            <TextField label="Pie (gracias, promos, horarios)" value={s.footerText ?? ''} onChange={(v) => set({ footerText: v })} />
          </section>

          {/* V180 — impresión de la comanda de cocina. */}
          <section className="rounded-xl border border-slate-200 bg-white p-4 space-y-2">
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Cocina</p>
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" className="mt-0.5" checked={s.printComanda !== false}
                onChange={(e) => set({ printComanda: e.target.checked })} />
              <span className="text-slate-700">
                Imprimir la <strong>comanda</strong> al enviar a cocina
                <span className="block text-[11px] text-slate-400">
                  Solo se imprime si esta estación tiene una impresora de <strong>Cocina</strong> asignada.
                  Sin impresora, «Enviar a cocina» manda el pedido a la pantalla de cocina sin imprimir (ni abrir el diálogo del navegador).
                </span>
              </span>
            </label>
          </section>

          {/* QRs */}
          <section className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Códigos QR</p>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={s.showInvoiceQr} onChange={(e) => set({ showInvoiceQr: e.target.checked })} />
              <span className="text-slate-700">Mostrar el QR de <strong>facturación</strong> (autofactura del cliente)</span>
            </label>
            {/* V153 (Pink) — sub-opción del bloque de facturación: imprimir la identidad
                fiscal del emisor (razón social + RFC). El plazo para autofacturar SIEMPRE
                se imprime. Default ON = comportamiento actual. */}
            {s.showInvoiceQr && (
              <label className="ml-6 flex items-start gap-2 text-sm">
                <input type="checkbox" className="mt-0.5" checked={s.showIssuerLine} onChange={(e) => set({ showIssuerLine: e.target.checked })} />
                <span className="text-slate-700">
                  Imprimir la línea del <strong>emisor</strong> (razón social + RFC)
                  <span className="block text-[11px] text-slate-400">El aviso del plazo para facturar se imprime siempre; esto solo oculta tu RFC.</span>
                </span>
              </label>
            )}
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={s.showCustomQr} onChange={(e) => set({ showCustomQr: e.target.checked })} />
              <span className="text-slate-700">Mostrar un QR <strong>configurable</strong> (Instagram, WhatsApp, menú, fidelidad…)</span>
            </label>
            {s.showCustomQr && (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <label className="text-sm">
                  <span className="text-xs font-medium text-slate-600">URL (con https://)</span>
                  <input className="input mt-1" value={s.customQrUrl ?? ''} onChange={(e) => set({ customQrUrl: e.target.value })} placeholder="https://instagram.com/minegocio" />
                </label>
                <label className="text-sm">
                  <span className="text-xs font-medium text-slate-600">Leyenda</span>
                  <input className="input mt-1" value={s.customQrLabel ?? ''} onChange={(e) => set({ customQrLabel: e.target.value })} placeholder="Síguenos en Instagram" maxLength={48} />
                </label>
              </div>
            )}
            {s.showInvoiceQr && s.showCustomQr && (
              <p className="text-[11px] text-slate-400">Van los dos, apilados uno bajo el otro con su leyenda y un separador.</p>
            )}
            {(s.showInvoiceQr || s.showCustomQr) && (
              <label className="flex items-center gap-2 text-sm">
                <span className="text-xs font-medium text-slate-600">Tamaño del QR</span>
                <select className="input w-auto" value={s.qrSizeMm} onChange={(e) => set({ qrSizeMm: Number(e.target.value) })}>
                  <option value={22}>Pequeño (~22 mm)</option>
                  <option value={26}>Mediano (~26 mm)</option>
                  <option value={30}>Grande (~30 mm)</option>
                  <option value={38}>Extra (~38 mm)</option>
                </select>
                <span className="text-[11px] text-slate-400">menos mm = menos papel</span>
              </label>
            )}
          </section>

          {canManage && (
            <div className="flex items-center gap-2">
              <button className={btnPrimary} onClick={save} disabled={saving}>{saving ? 'Guardando…' : 'Guardar'}</button>
              <button className={btnGhost} onClick={test}><Play className="h-3.5 w-3.5" /> Imprimir prueba</button>
              {msg && <span className="text-xs text-emerald-700">{msg}</span>}
            </div>
          )}
          {testResult && <TestBanner r={testResult} />}
        </div>

        {/* Preview 58 / 80 */}
        <div className="space-y-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Vista previa</p>
          <PreviewFrame title="58 mm" html={preview.w58} widthPx={230} />
          <PreviewFrame title="80 mm" html={preview.w80} widthPx={300} />
        </div>
      </div>
    </div>
  );
}

function PreviewFrame({ title, html, widthPx }: { title: string; html: string; widthPx: number }) {
  return (
    <div>
      <p className="mb-1 text-[11px] text-slate-500">{title}</p>
      <div className="overflow-hidden rounded-lg border border-slate-300 bg-white shadow-inner" style={{ width: widthPx }}>
        <iframe title={title} srcDoc={html} className="block w-full" style={{ height: 460, border: 0 }} />
      </div>
    </div>
  );
}

function TestBanner({ r }: { r: PrintOutcome }) {
  const good = r.ok && !r.usedFallback && r.method !== 'browser';
  const cls = good ? 'bg-emerald-50 text-emerald-800 ring-emerald-200' : 'bg-amber-50 text-amber-800 ring-amber-200';
  return (
    <div className={`flex items-start gap-2 rounded-xl px-3 py-2 text-xs ring-1 ${cls}`}>
      {good ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
      <span>{good ? `Prueba enviada a "${r.printerName}".` : `Se abrió la vista del navegador para imprimir la prueba. ${r.error ?? ''}`}</span>
    </div>
  );
}
